(() => {
  if (customElements.get('profile-products')) return;
  const cache = new Map();

  async function getProducts(base, profile) {
    const key = `${base}|${profile}`;
    if (cache.has(key)) return cache.get(key);
    const pending = (async () => {
      let url = new URL(base, window.location.origin);
      url.searchParams.set('section_id', 'profile-products');
      url.searchParams.set('filter.p.m.custom.product_profile', profile);
      // Retain a theme preview when Shopify represents it as a URL parameter.
      const preview = new URLSearchParams(window.location.search).get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      const records = [];
      const seen = new Set();
      const visited = new Set();
      while (url) {
        if (url.origin !== window.location.origin || visited.has(url.href)) throw new Error('Invalid profile pagination');
        visited.add(url.href);
        const response = await fetch(url.href, { credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Profile lookup unavailable');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const root = html.querySelector('[data-profile-results]');
        if (!root || root.dataset.enabled !== 'true' || (root.dataset.mode !== 'catalogue' && root.dataset.profile !== profile)) {
          throw new Error('Enable the Product profile filter in Search & Discovery and publish the products to Online Store for this preview.');
        }
        for (const record of root.querySelectorAll('[data-profile-product]')) {
          if (record.dataset.profile === profile && !seen.has(record.dataset.id)) {
            seen.add(record.dataset.id);
            records.push(record);
          }
        }
        const next = root.querySelector('[data-profile-next]');
        url = next ? new URL(next.getAttribute('href'), url) : null;
        if (url) {
          url.searchParams.set('section_id', 'profile-products');
          url.searchParams.set('filter.p.m.custom.product_profile', profile);
          if (preview) url.searchParams.set('preview_theme_id', preview);
        }
      }
      return records;
    })();
    cache.set(key, pending);
    pending.catch(() => cache.delete(key));
    return pending;
  }

  class ProfileProducts extends HTMLElement {
    connectedCallback() {
      if (this.started || this.hasAttribute('data-lazy')) return;
      // A closed colour disclosure has zero height; load it when it opens.
      const disclosure = this.closest('details');
      if (disclosure && !disclosure.open) {
        this.toggleHandler = () => { if (disclosure.open) this.load(); };
        this.disclosure = disclosure;
        disclosure.addEventListener('toggle', this.toggleHandler);
      } else {
        this.load();
      }
    }

    disconnectedCallback() {
      if (this.disclosure) this.disclosure.removeEventListener('toggle', this.toggleHandler);
    }

    async load() {
      if (this.started) return;
      this.started = true;
      try {
        const records = (await getProducts(this.dataset.url, this.dataset.profile))
          .filter(item => item.dataset.id !== this.dataset.product);
        if (!this.isConnected) return;
        if (this.dataset.mode === 'colours') {
          const rail = document.createElement('div');
          rail.className = 'profile-products__colours';
          for (const record of records) {
            const template = record.querySelector('template[data-colour-card]');
            if (template) rail.append(template.content.cloneNode(true));
          }
          this.replaceChildren(rail);
        } else {
          const colour = (this.dataset.colour || '').trim().toLocaleLowerCase();
          const sameColour = colour ? records.filter(item => item.dataset.colour.trim().toLocaleLowerCase() === colour) : [];
          // A sold-out exact colour remains browsable. Never substitute a different
          // colour simply because the matching colour cannot be purchased.
          const candidate = sameColour[0] || records.find(item => item.dataset.available === 'true') || records[0];
          const template = candidate?.querySelector('template[data-matching-card]');
          if (template) {
            this.replaceChildren(template.content.cloneNode(true));
            this.closest('[data-profile-matching]')?.removeAttribute('hidden');
            this.dispatchEvent(new CustomEvent('profile-products:loaded', { bubbles: true }));
          } else if (this.hasAttribute('data-editor')) {
            this.notice('No matching product is available to the Online Store in this market. Check product publication and the active profile reference.');
          }
        }
      } catch (error) {
        if (this.hasAttribute('data-editor')) this.notice(error.message);
      }
    }

    notice(message) {
      const notice = document.createElement('p');
      notice.className = 'profile-products__notice';
      notice.textContent = message;
      this.replaceChildren(notice);
      this.closest('[data-profile-matching]')?.removeAttribute('hidden');
    }
  }
  customElements.define('profile-products', ProfileProducts);
})();
